import {
  Category,
  DakenService,
  Environment,
  HubCoreService,
  Instagram,
  JessicaJonesService,
  LogService,
  MySquid,
  SocialNetworksAvailables,
  StatusProvider,
  TikTok,
  ToastHelper,
  Twitter,
  User,
  UserHelper,
  Youtube,
  localForageHelper,
  onConfigChange,
  onSocialNetworkConnection,
} from '@hub/shared'
import { CacheField } from '@src/components/profile-cache/profile-cache.component'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'

export interface Props extends React.HTMLAttributes<HTMLDivElement> {
  socialNetworkObject: Instagram | Youtube | Twitter | TikTok
  statusProvider?: StatusProvider
  user?: User
  type: SocialNetworksAvailables
  openCategory?: boolean
}

const useCardSocialNetwork = ({ className = '', socialNetworkObject, statusProvider, user, type, openCategory }: Props) => {
  const hubCoreService = useMemo(() => new HubCoreService(), [])
  const dakenService = useMemo(() => new DakenService(), [])
  const jessicaService = useMemo(() => new JessicaJonesService(), [])
  const userHelper = useMemo(() => new UserHelper(), [])
  const toast = useMemo(() => new ToastHelper().toast, [])
  const logService = useMemo(() => new LogService(), [])
  logService.init('Card Social Network Hook')

  const [t, i18n] = useTranslation('cardSocialNetwork')

  const [loadingRemoveProfile, setLoadingRemoveProfile] = useState(false)
  const [loadingButtonActionModal, setLoadingButtonActionModal] = useState<boolean>(false)
  const [loadingEditCategories, setLoadingEditCategories] = useState(false)
  const [loadingConfig, setLoadingConfig] = useState(true)
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false)
  const [openHasCampaignModal, setOpenHasCampaignModal] = useState(false)
  const [openEditCategories, setOpenEditCategories] = useState(openCategory ?? false)
  const [openCategories, setOpenCategories] = useState(false)
  const [categories, setCategories] = useState<Category[]>([])
  const [config, setConfig] = useState<MySquid | null>(null)
  const [socialNetwork, setSocialNetwork] = useState<SocialNetworksAvailables>('instagram')
  const [menuActions, setMenuActions] = useState<{ label: string; icon: string; iconText?: string; action: () => void }[]>([
    { label: t('actions.categories'), icon: 'fa-edit', action: () => setOpenEditCategories(true) },
    { label: t('actions.delete'), icon: 'fa-trash', action: () => setOpenDeleteConfirmation(true) },
  ])

  const removeAccountRequest = useCallback(
    async (idProfile: string, socialNetwork: string) => {
      try {
        await hubCoreService.deleteSocialNetwork(socialNetwork === 'instagram' ? 'facebook' : socialNetwork, idProfile)
      } catch (err) {
        logService.error({
          message: 'Failed to remove social network',
          error: err,
        })
        throw err
      }
    },
    [hubCoreService, logService]
  )

  const refreshToken = useCallback(async () => {
    const refreshToken = await jessicaService?.refreshToken()
    const userInfo = await dakenService?.getInfluencersInfo(refreshToken?.idToken)
    if (userInfo) {
      await userHelper.setUserObject(userInfo, refreshToken?.idToken, refreshToken?.expiresIn)
      await userHelper.setUser(userInfo)
    }
  }, [jessicaService, dakenService, userHelper])

  const removeProfile = useCallback(
    async (idProfile: string, socialNetwork: string): Promise<void> => {
      if (!loadingRemoveProfile) {
        setLoadingRemoveProfile(true)
        try {
          setLoadingButtonActionModal(true)
          await removeAccountRequest(idProfile, socialNetwork)
          await refreshToken()
          setOpenDeleteConfirmation(false)
          toast.success(t('deleted'), {
            closeButton: true,
            className: 'full text-bold',
            position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
            notOverClick: true,
            immediately: true,
          })
        } catch (error) {
          setOpenDeleteConfirmation(false)
          toast.error(t('errors.profileDeletionError'), {
            closeButton: true,
            className: 'full',
            position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
            duration: 3600000,
            notOverClick: true,
            immediately: true,
          })
        } finally {
          setLoadingButtonActionModal(false)
        }
        setLoadingRemoveProfile(false)
      }
    },
    [loadingRemoveProfile, removeAccountRequest, toast, t, refreshToken]
  )

  const onUpdateCategories = useCallback(
    (newCategories: Category[]) => {
      setOpenEditCategories(false)
      setCategories(newCategories)
    },
    [setOpenEditCategories, setCategories]
  )

  const onRenewToken = useCallback(async (idProfile: string, socialNetwork: string) => {
    interface WindowWithEnvironments extends Window {
      environments?: Environment
    }
    const thisWindow: WindowWithEnvironments = window
    await localForageHelper.setItem('actions', { action: 'renew', idProfile })
    window.location.href = `${thisWindow?.environments?.squidApis?.proxy}/${
      socialNetwork === 'instagram' ? 'facebook' : socialNetwork
    }?squid-callback=${window.location.href}`
  }, [])

  const handleRenewToken = useCallback(
    async (idProfile: string, socialNetwork: string) => {
      await localForageHelper.setItem('actions', { action: 'renew', idProfile })
      if (['instagram', 'facebook'].includes(socialNetwork)) {
        onSocialNetworkConnection.next({
          socialNetwork: socialNetwork as SocialNetworksAvailables,
          connect: true,
        })
      } else {
        await onRenewToken(idProfile, socialNetwork)
      }
    },
    [onRenewToken]
  )

  const isTokenValid = useCallback(() => {
    if (!statusProvider) {
      return false
    }
    if (socialNetworkObject?.macro) {
      return true
    }
    return statusProvider?.tokenExpired === false
  }, [socialNetworkObject, statusProvider])

  const fieldsCache = useMemo((): CacheField[] => {
    const fieldsStructure: { [key: string]: CacheField[] } = {
      instagram: ['posts', 'reels', 'stories'],
      // ? Os itens abaixo ainda não estão disponíveis
      youtube: ['videos'],
      twitter: ['tweets'],
      tiktok: ['videos'],
    }
    return fieldsStructure?.[socialNetwork as keyof typeof fieldsStructure]
  }, [socialNetwork])

  useEffect(() => {
    setMenuActions(
      menuActions?.map((m) => {
        let key = 'actions.categories'
        if ('fa-trash'.includes(m.icon)) {
          key = 'actions.delete'
        }
        return {
          ...m,
          label: t(key),
        }
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, isTokenValid])

  useEffect(() => {
    const socialNetworkValue = type === 'facebook' ? 'instagram' : type
    setSocialNetwork(socialNetworkValue)
  }, [type])

  useEffect(() => {
    if (!loadingEditCategories) {
      setLoadingEditCategories(true)
      dakenService
        .getCategoriesBySocialNetwork(socialNetworkObject?.id)
        .then((r: Category[]) => {
          setCategories(r)
        })
        .catch((error: any) => {
          logService.error({ message: 'Failed to get categories', error })
        })
        .finally(() => {
          setLoadingEditCategories(false)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socialNetworkObject, logService])

  useEffect(() => {
    const onConfigSubscription: SubscriptionLike = onConfigChange?.subscribe((configChanged) => {
      if (typeof configChanged !== 'boolean' && configChanged?._id) {
        setConfig(configChanged)
      }
      setLoadingConfig(false)
    })

    return () => onConfigSubscription?.unsubscribe()
  }, [])

  return {
    props: {
      className,
      socialNetworkObject,
      statusProvider,
      user,
      type,
      openCategory,
    },
    memo: {
      fieldsCache,
    },
    state: {
      loadingEditCategories,
      categories,
      socialNetwork,
      openCategories,
      setOpenCategories,
      openEditCategories,
      setOpenEditCategories,
      openDeleteConfirmation,
      setOpenDeleteConfirmation,
      openHasCampaignModal,
      setOpenHasCampaignModal,
      config,
      setConfig,
      loadingConfig,
      setLoadingConfig,
      loadingButtonActionModal,
      setLoadingButtonActionModal,
      menuActions,
      setMenuActions,
    },
    functions: {
      removeAccountRequest,
      removeProfile,
      onUpdateCategories,
      handleRenewToken,
      isTokenValid,
    },
  }
}

export default useCardSocialNetwork
